import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SERVICE_MAP = {
  antstalk: { name: 'AntsTalk', color: '#facc15', icon: '💬' },
  antsmusic: { name: 'AntsMusic', color: '#8b5cf6', icon: '🎵' },
  // 스카이스크라이브 이름으로 쌓인 기록이 3천 건 넘게 남아 있다.
  // 키를 지우면 그 기록이 이름 없는 항목이 되므로 함께 유지한다.
  skyscribe: { name: 'SkyScribe', color: '#8b5cf6', icon: '🎵' },
  nodemaster: { name: 'NodeMaster', color: '#3b82f6', icon: '📁' },
  edu: { name: 'Edu', color: '#10b981', icon: '📚' },
  store: { name: 'Store', color: '#f97316', icon: '🛒' },
  gantt: { name: 'GanttChart', color: '#06b6d4', icon: '📊' },
  brand: { name: 'Brand', color: '#ec4899', icon: '🏷️' },
};

const ACTION_LABELS = {
  LOGIN: '로그인', LOGOUT: '로그아웃', REGISTER: '회원가입',
  PROFILE_UPDATE: '프로필 수정', PASSWORD_CHANGE: '비밀번호 변경',
  FRIEND_REQUEST: '친구 요청', FRIEND_ACCEPT: '친구 수락',
  CHAT_CREATE: '채팅방 생성', MESSAGE_SEND: '메시지 전송',
  POST_CREATE: '게시글 작성', POST_DELETE: '게시글 삭제',
  FILE_CREATE: '파일 생성', FILE_DELETE: '파일 삭제',
  SONG_LIKE: '곡 좋아요', ALBUM_CREATE: '앨범 생성',
};

function formatDate(s) {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const ACCOUNT_TYPE_BADGE = {
  regular: { label: '일반', color: '#3b82f6' },
  google: { label: 'Google', color: '#ea4335' },
  kakao: { label: 'Kakao', color: '#facc15', textColor: '#000' },
  apple: { label: 'Apple', color: '#d1d5db' },
};

export default function Users() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [accountType, setAccountType] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedStats, setSelectedStats] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 30,
        accountType,
        search,
      });
      const res = await fetch(`/api/admin/users?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setItems(data.data);
        setPagination((p) => ({ ...p, totalPages: data.pagination.totalPages, total: data.pagination.total }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, accountType, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (user) => {
    setSelected(user);
    setSelectedStats(null);
    setSelectedActivities([]);
    setActivitiesLoading(true);
    try {
      const [statsRes, actRes] = await Promise.all([
        fetch(`/api/admin/user-stats/${user.user_id}`, { credentials: 'include' }),
        fetch(`/api/admin/activity-log?userId=${user.user_id}&limit=15`, { credentials: 'include' }),
      ]);
      const statsData = await statsRes.json();
      const actData = await actRes.json();
      if (statsData.success) setSelectedStats(statsData.stats);
      if (actData.success) setSelectedActivities(actData.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleDeactivate = async (user) => {
    if (user.user_account === 'admin@antsnest.co.kr') {
      alert('관리자 계정은 탈퇴 처리할 수 없습니다.');
      return;
    }
    const displayName = user.nickname || user.user_name || user.user_account;

    const confirmed = window.confirm(
      `⚠️ 정말 [${displayName}] 님을 탈퇴 처리하시겠습니까?\n\n` +
      `· 해당 사용자는 모든 서비스에서 로그인이 차단됩니다.\n` +
      `· 데이터(채팅/게시물/친구관계)는 보존되며 "복원" 으로 되돌릴 수 있습니다.`
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`/api/admin/users/${user.user_id}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: null }),
      });
      const data = await res.json();
      if (data.success) {
        alert('탈퇴 처리되었습니다.');
        setSelected(null);
        load();
      } else {
        alert(data.message || '탈퇴 처리 실패');
      }
    } catch (e) {
      alert('서버 오류: ' + e.message);
    }
  };

  const handleReactivate = async (user) => {
    if (!window.confirm(`${user.nickname || user.user_name || user.user_account}님을 복원하시겠습니까?`)) return;
    try {
      const res = await fetch(`/api/admin/users/${user.user_id}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        alert('복원되었습니다.');
        setSelected(null);
        load();
      } else {
        alert(data.message || '복원 실패');
      }
    } catch (e) {
      alert('서버 오류: ' + e.message);
    }
  };

  return (
    <>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">사용자 관리</h1>
          <p className="admin-page-desc">전체 SSO 사용자 목록 및 활동 통계 · 총 {pagination.total.toLocaleString()}명</p>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="admin-filter-bar" style={{ marginBottom: 12 }}>
          {['all', 'regular', 'google', 'kakao', 'apple'].map((t) => (
            <button
              key={t}
              className={'admin-filter-pill' + (accountType === t ? ' active' : '')}
              onClick={() => { setAccountType(t); setPagination((p) => ({ ...p, page: 1 })); }}
            >{t === 'all' ? '전체' : (ACCOUNT_TYPE_BADGE[t]?.label || t)}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="이름/이메일/아이디 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPagination((p) => ({ ...p, page: 1 })); } }}
            className="admin-input"
            style={{ flex: 1 }}
          />
          <button
            className="admin-filter-pill active"
            onClick={() => { setSearch(searchInput); setPagination((p) => ({ ...p, page: 1 })); }}
          >검색</button>
        </div>
      </div>

      <div className="admin-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>사용자</th>
                <th>계정</th>
                <th>이메일</th>
                <th>아이디</th>
                <th>가입일</th>
                <th>최근 로그인</th>
                <th>상태</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>불러오는 중...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>사용자가 없습니다.</td></tr>
              ) : items.map((u) => {
                const badge = ACCOUNT_TYPE_BADGE[u.account_type] || { label: u.account_type, color: '#6b7280' };
                return (
                  <tr key={u.user_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} />
                        ) : (
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>?</div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: '#fff' }}>{u.nickname || u.user_name || '-'}</div>
                          {u.department && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{u.department}{u.position ? ` / ${u.position}` : ''}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 4,
                        background: badge.color + '22',
                        color: badge.textColor || badge.color,
                        fontSize: 10.5,
                        fontWeight: 700,
                        border: `1px solid ${badge.color}44`,
                        letterSpacing: 0.3,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{u.user_email || '-'}</td>
                    <td style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{u.user_login_id || u.user_account || '-'}</td>
                    <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{formatDate(u.created_at)}</td>
                    <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{u.last_login_at ? formatDate(u.last_login_at) : '-'}</td>
                    <td>
                      {u.use_yn === 'Y' && u.status === 'active' ? (
                        <span style={{ color: '#34d399', fontSize: 11, fontWeight: 600 }}>● 활성</span>
                      ) : (
                        <span style={{ color: '#fca5a5', fontSize: 11, fontWeight: 600 }}>● 비활성</span>
                      )}
                    </td>
                    <td>
                      <button className="admin-filter-pill" onClick={() => openDetail(u)}>상세</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, alignItems: 'center' }}>
          <button className="admin-filter-pill" disabled={pagination.page <= 1} onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}>이전</button>
          <span style={{ padding: '6px 12px', fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>{pagination.page} / {pagination.totalPages}</span>
          <button className="admin-filter-pill" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}>다음</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <div style={modalOverlay} onClick={() => setSelected(null)}>
          <div className="admin-card" style={modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#fff' }}>사용자 상세</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                {selected.use_yn === 'Y' && selected.status === 'active' ? (
                  <button
                    onClick={() => handleDeactivate(selected)}
                    style={{ background: 'rgba(248, 113, 113, 0.1)', border: '1px solid rgba(248, 113, 113, 0.3)', color: '#fca5a5', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >탈퇴 처리</button>
                ) : (
                  <button
                    onClick={() => handleReactivate(selected)}
                    style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', color: '#34d399', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >복원</button>
                )}
                <button onClick={() => setSelected(null)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              {selected.avatar_url ? (
                <img src={selected.avatar_url} alt="" style={{ width: 60, height: 60, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)' }} />
              ) : (
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
              )}
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{selected.nickname || selected.user_name || '-'}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{selected.user_email}</div>
              </div>
            </div>
            <table style={{ width: '100%', fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
              <tbody>
                <DetailRow label="UUID" value={<code style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)' }}>{selected.user_id}</code>} />
                <DetailRow label="로그인 ID" value={selected.user_account} />
                <DetailRow label="아이디 (영문)" value={selected.user_login_id || '-'} />
                <DetailRow label="이메일" value={selected.user_email || '-'} />
                <DetailRow label="전화번호" value={selected.user_mobile || '-'} />
                <DetailRow label="가입 방식" value={`${selected.account_type}${selected.oauth_provider ? ` (${selected.oauth_provider})` : ''}`} />
                {selected.migrated_from && (
                  <DetailRow label="마이그레이션" value={
                    <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', fontSize: 11, fontWeight: 600 }}>
                      {selected.migrated_from} 에서 이전 {selected.original_id ? `(원본 ID: ${selected.original_id})` : ''}
                    </span>
                  } />
                )}
                <DetailRow label="가입일" value={formatDate(selected.created_at)} />
                <DetailRow label="최근 로그인" value={selected.last_login_at ? formatDate(selected.last_login_at) : '-'} />
              </tbody>
            </table>
            {selectedStats && (
              <div style={{ marginTop: 20, padding: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>활동 통계</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, fontSize: 12 }}>
                  <StatMini label="총 활동" value={Number(selectedStats.total_activities || 0).toLocaleString()} />
                  <StatMini label="최근 7일" value={Number(selectedStats.last_7d || 0).toLocaleString()} />
                  <StatMini label="주 사용 서비스" value={selectedStats.most_used_service || '-'} />
                  <StatMini label="마지막 활동" value={selectedStats.last_activity ? formatDate(selectedStats.last_activity) : '-'} small />
                </div>
              </div>
            )}

            {/* 최근 활동 내역 */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>최근 활동 (최대 15건)</h3>
                <button
                  className="admin-filter-pill active"
                  onClick={() => { setSelected(null); navigate(`/Admin/activity?userId=${selected.user_id}`); }}
                  style={{ fontSize: 11 }}
                >전체 활동 보기 →</button>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', maxHeight: 280, overflowY: 'auto' }}>
                {activitiesLoading ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>불러오는 중...</div>
                ) : selectedActivities.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>활동 내역이 없습니다</div>
                ) : (
                  selectedActivities.map((act) => {
                    const svc = SERVICE_MAP[act.service_code] || { name: act.service_code, color: '#6b7280', icon: '🔹' };
                    return (
                      <div key={act.id} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 999, background: svc.color + '22', color: svc.color, fontSize: 10, fontWeight: 600, border: `1px solid ${svc.color}44`, whiteSpace: 'nowrap' }}>
                          {svc.icon} {svc.name}
                        </span>
                        <span style={{ color: '#fff', flex: 1 }}>{ACTION_LABELS[act.action] || act.action}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10.5, whiteSpace: 'nowrap' }}>{formatDate(act.created_at)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DetailRow({ label, value }) {
  return (
    <tr>
      <td style={{ padding: '7px 0', color: 'rgba(255,255,255,0.45)', width: 130, fontSize: 12 }}>{label}</td>
      <td style={{ padding: '7px 0' }}>{value}</td>
    </tr>
  );
}

function StatMini({ label, value, small }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: small ? 12 : 18, fontWeight: small ? 500 : 700, color: '#fff' }}>{value}</div>
    </div>
  );
}

const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,15,35,0.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 };
const modalContent = { padding: 28, maxWidth: 540, width: '92%', maxHeight: '88vh', overflow: 'auto' };
